import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import {
  KubernetesActionMenu,
  KubernetesActionConfirmationDialog,
  KubernetesConnectionDialog,
} from '../actions';
import { KubernetesTableData } from './types';
import {
  mapResourcesToKubernetesTableData,
  getTeleportDocsUrl,
  getK9sDocsUrl,
} from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  KubernetesResourceDetails,
  CRDKubernetesConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import {
  useApi,
  errorApiRef,
  alertApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { useKubernetesContext } from '../KubernetesContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface KubernetesTableProps {
  entityRef: string;
  kubernetesConfig: CRDKubernetesConfig;
  title: string;
}

export const KubernetesTable: React.FC<KubernetesTableProps> = ({
  entityRef,
  kubernetesConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useKubernetesContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'kubernetes',
      resourceType: 'resources',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: kubernetesConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for Kubernetes resources
    api.invalidateCache('listResources');
    refresh();
  };

  const config = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    kubernetesConnectionDialogOpen,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    handleOpenConnection,
    setConfirmationDialogOpen,
    setKubernetesConnectionDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    kubernetesConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `kubernetes:resources:${kubernetesConfig.kubeContext}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, kubernetesConfig.kubeContext]);

  // Move useMemo hooks before conditional returns
  // Columns for Kubernetes
  const columns = useMemo<TableColumn<KubernetesTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: KubernetesTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Kind',
        field: 'kind',
      },
      {
        title: 'Namespace',
        field: 'namespace',
      },

      {
        title: 'Replicas',
        field: 'replicas',
        render: (row: KubernetesTableData) => `${row.ready}/${row.replicas}`,
      },
      {
        title: 'Status',
        field: 'status',
      },
      {
        title: 'Previous Desired Size',
        field: 'previousDesiredSize',
        render: (row: KubernetesTableData) =>
          row.previousDesiredSize !== undefined
            ? row.previousDesiredSize
            : 'N/A',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToKubernetesTableData(
            resources as KubernetesResourceDetails[],
          )
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load Kubernetes resources`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<KubernetesTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="kubernetes"
        resourceType="resources"
        columns={columns}
        colorTheme="#326CE5" // Kubernetes Blue
        emptyMessage="No Kubernetes resources found"
      />

      <KubernetesActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={handleOpenConnection}
        menuPosition={menuPosition}
        service="resources"
      />

      <KubernetesActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="resources"
      />

      <KubernetesConnectionDialog
        open={kubernetesConnectionDialogOpen}
        onClose={() => setKubernetesConnectionDialogOpen(false)}
        teleportUrl={getTeleportDocsUrl(config)}
        k9sUrl={getK9sDocsUrl(config)}
      />
    </>
  );
};
